import React, {
  useImperativeHandle,
  useState,
  forwardRef,
  useEffect,
  Dispatch,
  SetStateAction,
} from 'react'
import { Divider, List } from '@mui/material'
import { styled } from '@mui/material/styles'
import { HallPermissions } from '@weblite/ts-action/hall'
import { SettingsSection } from '$common/settings-section'
import { LabeledSwitch } from '$common/labeled-switch'
import { RoomPermission } from '$logic/room'

const StyledDivider = styled(Divider)`
  margin-right: 20px;
  &:last-child {
    display: none;
  }
`

export interface Props<Right, Permissions> {
  rights: Right[]
  initialPermissions: Permissions
  subHeader: string
  setIsAllChecked: Dispatch<SetStateAction<boolean>>
}

export type RoomPermissionsRef<Permissions extends Partial<HallPermissions>> = {
  getValues: () => Permissions | undefined
}

export const RoomPermissions = forwardRef(function RoomPermissions<
  Right extends {
    id: keyof HallPermissions
    isLocked: boolean
    localId: string
    requires?: string
  },
  Permissions extends Partial<HallPermissions>
>({ rights, initialPermissions, subHeader, setIsAllChecked }: Props<Right, Permissions>, ref: React.Ref<RoomPermissionsRef<Permissions>>) {
  const [permissionValues, setPermissionValues] = useState<Permissions>()

  useEffect(() => {
    if (permissionValues || !initialPermissions) return
    setPermissionValues(initialPermissions)
  }, [permissionValues, initialPermissions])

  useEffect(() => {
    if (permissionValues)
      if (
        Object.values(permissionValues).filter(value => !!value).length ===
        Object.values(permissionValues).length
      )
        setIsAllChecked(true)
      else setIsAllChecked(false)
  }, [permissionValues])

  useImperativeHandle(ref, () => ({ getValues: () => permissionValues }), [
    permissionValues,
  ])

  const toggleSwitch = (permissionId: keyof HallPermissions) => {
    if (permissionValues?.[permissionId]) {
      setPermissionValues(
        rights
          .filter(({ requires }) => requires === permissionId)
          .reduce((acc, { id }) => ({ ...acc, [id]: false }), {
            ...permissionValues,
            [permissionId]: false,
          }),
      )
    } else {
      const { requires } = rights.find(({ id }) => id === permissionId) || {}
      setPermissionValues({
        ...permissionValues,
        [permissionId]: true,
        ...(requires && { [requires]: true }),
      } as Permissions)
    }
  }

  // TODO: Export texts to i18n
  return (
    <SettingsSection headerText={subHeader} sx={{ marginBottom: '10px' }}>
      <List disablePadding>
        {rights.map(({ localId, id, isLocked }) => (
          <>
            <LabeledSwitch
              checked={!!permissionValues?.[id]}
              disabled={isLocked}
              label={localId}
              onChange={() => toggleSwitch(id)}
            />
            <StyledDivider />
          </>
        ))}
      </List>
    </SettingsSection>
  )
})
